import { Component, effect, EventEmitter, input, OnInit, output, Output, signal } from '@angular/core';
import {
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  PatientBehaviorDataModel,
  PatientBehaviorLocationModel,
  PatientBehaviorModel,
  PatientBehaviorResponseModel,
  PatientBehaviorSeverityModel,
  PatientBehaviorType,
  PatientSessionModel,
  PatientTargetDataModel,
  PatientTargetModel,
  PromptCodeModel,
  TargetType,
} from '../../../core/api/models';
import { Utils } from '../../../core/utils';
import {
  StaffApiService,
  SupervisorApiService,
} from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { AuthService } from '../../../auth/auth.service';
import { TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { targetIsEchoic, targetIsPromptBased } from '../../../shared/utils/utils';

@Component({
  selector: 'app-session',
  templateUrl: './session.component.html',
  styleUrl: './session.component.css',
  standalone: false,
})
export class SessionComponent implements OnInit {
  isLoading: boolean = true;

  showTargetGraph = signal(false);
  showBehaviorGraph = signal(false);

  session = input.required<PatientSessionModel>();

  targetsData: PatientTargetDataModel[] = [];
  behaviorsData: PatientBehaviorDataModel[] = [];

  targets = input.required<PatientTargetModel[]>();
  behaviors = input.required<PatientBehaviorModel[]>();
  antecedents = input.required<PatientBehaviorAntecedentModel[]>();
  consequences = input.required<PatientBehaviorConsequenceModel[]>();
  locations = input.required<PatientBehaviorLocationModel[]>();
  responses = input.required<PatientBehaviorResponseModel[]>();
  severities = input.required<PatientBehaviorSeverityModel[]>();
  defaultPromptCodes = input.required<PromptCodeModel[]>();

  targetDurationInMinutes = signal<number>(0);
  behaviorDurationInMinutes = signal<number>(0);
  fluencyDurationInMinutes = signal<number>(0);
  fluencyCorrectCount = signal<number>(0);
  fluencyIncorrectCount = signal<number>(0);

  selectedTarget: PatientTargetModel | null = null;
  selectedBehavior: PatientBehaviorModel | null = null;
  clonedSessionData: {
    [s: number]: PatientTargetDataModel | PatientBehaviorDataModel;
  } = {};

  selectedData: PatientTargetDataModel[] | PatientBehaviorDataModel[] = [];

  sessionBeingEdited: PatientSessionModel | null = null;

  wholePartialIntervalHappened: boolean = false;

  @Output() onSessionDataDeleted = new EventEmitter<boolean>();

  constructor(
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    private authService: AuthService
  ) {
    effect(() => {
      const session = this.session();
      console.log('Session effect triggered - Session ID:', session.ID);
      console.log('Session Targets:', session.TargetData);
      console.log('Session Behaviors:', session.BehaviorData);
      this.targetsData = session.TargetData ?? [];
      this.behaviorsData = session.BehaviorData ?? [];
    });
  }

  ngOnInit(): void {
    if (this.targets().length === 0) {
      console.log('Targets not provided, fetching from API');
      this.isLoading = true;
    } else {
      console.log('Targets provided:', this.targets());
    }
  }

  getTargetName(targetId: number) {
    const target = this.targets().find((t) => t.ID === targetId);
    return target?.TargetName ?? 'Unknown';
  }

  getTargetType(targetId: number) {
    const target = this.targets().find((t) => t.ID === targetId);
    const targetName = Utils.patientTargetTypeLabel(target?.TargetType!);
    return targetName;
  }

  targetIsPromptBased(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return targetIsPromptBased(target);
  }

  targetIsEchoic(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return targetIsEchoic(target);
  }

  canPlotTargetGraph(targetId: number) {
    const target = this.targets().find((t) => t.ID === targetId);
    return target?.TargetType ? Utils.canPlotGraph(target.TargetType) : false;
  }

  onShowTargetGraphClicked(targetId: number | null) {
    this.selectedTarget = this.targets().find((t) => t.ID === targetId) ?? null;
    this.showTargetGraph.update((oldValue) => !oldValue);
  }

  onShowBehaviorGraphClicked(behaviorId: number | null) {
    this.selectedBehavior = this.behaviors().find((b) => b.ID === behaviorId) ?? null;
    this.showBehaviorGraph.update((oldValue) => !oldValue);
  }

  hideGraphs() {
    this.showTargetGraph.set(false);
    this.showBehaviorGraph.set(false);
  }

  firstColorForEchoic(targetData: PatientTargetDataModel) {
    return this.valueColorForTarget(targetData);
  }

  firstTextForEchoic(targetData: PatientTargetDataModel) {
    return this.valueTextForTarget(targetData);
  }

  secondColorForEchoic(targetData: PatientTargetDataModel) {
    return '#5DADE2';
  }

  secondTextForEchoic(targetData: PatientTargetDataModel) {
    return targetData.TextValue?.split('|')[1] ?? 'Unknown';
  }

  valueTextForTarget(targetData: PatientTargetDataModel) {
    // if type uses prompt codes, use it for the text, otherwise use the int value.
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    if (this.targetIsPromptBased(targetData)) {
      var promptCodeForTargetValue = target?.PromptCodes?.find(
        (p) => p.ID === targetData.IntValue
      );

      if (promptCodeForTargetValue == null) {
        // look for the prompt code in the prompt codes list
        promptCodeForTargetValue = this.defaultPromptCodes().find(
          (p) => p.ID === targetData.IntValue
        );
      }

      return promptCodeForTargetValue?.Text ?? 'Unknown - ' + targetData.TextValue;
    } else {
      if (this.targetIsIntValueOnly(targetData)) {
        return targetData.IntValue?.toString() ?? 'No Value For Target';
      }
      else if (this.targetIsToileting(targetData)) {
        return targetData.IntValue ? this.secondsToFormattedString(targetData.IntValue) : 'No Value For Target';
      } else if (this.targetIsWholePartialInterval(targetData)) {
        return (targetData.IntValue == 0 ? 'Did not happen ' : 'Happened') + ' (current interval is ' + this.secondsToFormattedString(target?.Duration!) + ' seconds)';
      } else if (this.targetIsFluencyRate(targetData)) {
        return this.fluencyRateValueText(targetData);
      } else {
        return targetData.TextValue ?? 'No Value For Target';
      }
    }
  }

  secondsToFormattedString(seconds: number) {
    const minutes = Math.floor((seconds % 3600) / 60);
    const seconds1 = seconds % 60;
    return `${minutes < 10 ? '0' + minutes : minutes}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
  }

  fluencyRateValueText(targetData: PatientTargetDataModel) {
    const value = targetData.TextValue ?? 'No Value For Target';
    if (!targetData.Duration || targetData.Duration <= 0) {
      return value;
    }

    return `${value} (${this.secondsToFormattedString(targetData.Duration)})`;
  }

  parseFluencyRateTextValue(textValue: string | null | undefined) {
    const [correct, incorrect] = (textValue ?? '0/0').split('/').map((value) => Number(value));
    return {
      correct: Number.isFinite(correct) ? correct : 0,
      incorrect: Number.isFinite(incorrect) ? incorrect : 0,
    };
  }

  formatManuallyAddedDate(date: string | null | undefined): string {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
  }

  get isAnyRowEditing(): boolean {
    return Object.keys(this.clonedSessionData).length > 0;
  }
  
  getUserNameFromViewModel(user: any): string {
    if (!user) return 'N/A';
    if (user.FirstName || user.LastName) {
      return `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.UserName || 'N/A';
    }
    return user.UserName || 'N/A';
  }

  targetIsIntValueOnly(targetData: PatientTargetDataModel) {
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return target?.TargetType === TargetType.Quantity;
  }

  valueColorForTarget(targetData: PatientTargetDataModel) {
    // if type uses prompt codes, use it for the text, otherwise use the int value.
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    if (this.targetIsPromptBased(targetData)) {
      var promptCodeForTargetValue = target?.PromptCodes?.find(
        (p) => p.ID === targetData.IntValue
      );

      if (promptCodeForTargetValue == null) {
        // look for the prompt code in the prompt codes list
        promptCodeForTargetValue = this.defaultPromptCodes().find(
          (p) => p.ID === targetData.IntValue
        );
      }

      return promptCodeForTargetValue?.Color ?? 'black';
    } else {
      return 'black';
    }
  }

  promptOptionsForTarget(targetData: PatientTargetDataModel) {
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    const promptOptionsForTarget = target?.PromptCodes?.map((p) => {
      return {
        value: p.ID,
        text: p.Text,
      };
    });
    return promptOptionsForTarget;
  }

  extraOptionsForEchoicTarget(targetData: PatientTargetDataModel) {
    const firstPartOfValue = targetData.TextValue?.split('|')[0];
    const extraOptionsForEchoicTarget = [
      {
        value: firstPartOfValue + '|Articulation',
        text: 'Articulation',
      },
      {
        value: firstPartOfValue + '|Volume',
        text: 'Volume',
      },
      {
        value: firstPartOfValue + '|Speed',
        text: 'Speed',
      },
      {
        value: firstPartOfValue + '|Tone',
        text: 'Tone',
      },
      {
        value: firstPartOfValue + '|Rhythm',
        text: 'Rhythm',
      },
    ];
    return extraOptionsForEchoicTarget;
  }

  getBehaviorName(behaviorId: number) {
    const behavior = this.behaviors().find((b) => b.ID === behaviorId);
    return behavior?.PatientBehaviorName ?? 'Unknown';
  }

  getAntecedentsLabelsName(
    antecedentIDs: number[],
    antecedentOther: string | null
  ) {
    if (antecedentIDs.length === 0 && !antecedentOther) {
      return 'None';
    }
    var antecedentsLabel = antecedentIDs
      .map((antecedentId) => {
        const antecedent = this.antecedents().find(
          (a) => a.ID === antecedentId
        );
        return antecedent?.PatientBehaviorAntecedent ?? 'Unknown';
      })
      .join(', ');
    if (antecedentOther) {
      if (antecedentsLabel === '') {
        antecedentsLabel += antecedentOther;
      } else {
        antecedentsLabel += ', ' + antecedentOther;
      }
    }
    return antecedentsLabel;
  }

  getConsequencesLabelsName(
    consequenceIDs: number[],
    consequenceOther: string | null
  ) {
    if (consequenceIDs.length === 0 && !consequenceOther) {
      return 'None';
    }
    var consequenceLabel = consequenceIDs
      .map((consequenceId) => {
        const consequence = this.consequences().find(
          (c) => c.ID === consequenceId
        );
        return consequence?.PatientBehaviorConsequence ?? 'Unknown';
      })
      .join(', ');
    if (consequenceOther) {
      if (consequenceLabel === '') {
        consequenceLabel += consequenceOther;
      } else {
        consequenceLabel += ', ' + consequenceOther;
      }
    }
    return consequenceLabel;
  }

  getLocationName(locationId: number | null) {
    if (!locationId) {
      return 'None';
    }
    const location = this.locations().find((l) => l.ID === locationId);
    return location?.PatientBehaviorLocation ?? 'Unknown';
  }

  getResponseName(responseId: number | null) {
    if (!responseId) {
      return 'None';
    }
    const response = this.responses().find((r) => r.ID === responseId);
    return response?.PatientBehaviorResponse ?? 'Unknown';
  }

  getSeverityName(severityId: number | null) {
    if (!severityId) {
      return 'None';
    }
    const severity = this.severities().find((s) => s.ID === severityId);
    return severity?.PatientBehaviorSeverity ?? 'Unknown';
  }

  onSessionDataEditInit(
    sessionData: PatientTargetDataModel | PatientBehaviorDataModel
  ) {
    console.log('Editing session data:', sessionData);

    this.sessionBeingEdited = this.session();

    if (this.isPatientTargetDataModel(sessionData)) {
      if (this.targetIsDuration(sessionData)) {
        this.targetDurationInMinutes.set(this.secondsToMinutes(sessionData.IntValue!));
      }
      if (this.targetIsFluencyRate(sessionData)) {
        const fluencyValues = this.parseFluencyRateTextValue(sessionData.TextValue);
        this.fluencyCorrectCount.set(fluencyValues.correct);
        this.fluencyIncorrectCount.set(fluencyValues.incorrect);
        this.fluencyDurationInMinutes.set(this.secondsToMinutes(sessionData.Duration ?? 0));
      }
      if (this.targetIsWholePartialInterval(sessionData)) {
        this.wholePartialIntervalHappened = sessionData.IntValue === 0 ? false : true;
      }
    } else if (this.isPatientBehaviorDataModel(sessionData)) {
      if (this.behaviorIsDuration(sessionData)) {
        this.behaviorDurationInMinutes.set(this.secondsToMinutes(sessionData.IntValue ?? 0));
      }
    }

    this.clonedSessionData[sessionData.ID as number] = {
      ...sessionData,
    } as PatientTargetDataModel | PatientBehaviorDataModel;
  }

  isPatientTargetDataModel(obj: any): obj is PatientTargetDataModel {
    return 'PatientSessionID' in obj && 'PatientTargetID' in obj;
  }

  onSessionDataEditSave(
    sessionData: PatientTargetDataModel | PatientBehaviorDataModel
  ) {

    if (this.isPatientTargetDataModel(sessionData)) {
      if (this.targetIsDuration(sessionData)) {
        const intValueInSeconds = this.minutesToSeconds(this.targetDurationInMinutes());
        const textValue = this.minutesToFormattedString(this.targetDurationInMinutes());
        
        // Update the passed object
        sessionData.IntValue = intValueInSeconds;
        sessionData.TextValue = textValue;
        
        // Also update the object in the array to ensure it's saved correctly
        const targetIndex = this.targetsData.findIndex((t) => t.ID === sessionData.ID);
        if (targetIndex !== -1) {
          this.targetsData[targetIndex].IntValue = intValueInSeconds;
          this.targetsData[targetIndex].TextValue = textValue;
        }
        
        // Also update in the session being edited
        if (this.sessionBeingEdited && this.sessionBeingEdited.TargetData) {
          const sessionTargetIndex = this.sessionBeingEdited.TargetData.findIndex(
            (t) => t.ID === sessionData.ID
          );
          if (sessionTargetIndex !== -1) {
            this.sessionBeingEdited.TargetData[sessionTargetIndex].IntValue = intValueInSeconds;
            this.sessionBeingEdited.TargetData[sessionTargetIndex].TextValue = textValue;
          }
        }
      }
      if (this.targetIsWholePartialInterval(sessionData)) {
        const intValue = this.wholePartialIntervalHappened ? 1 : 0;
        sessionData.IntValue = intValue;
        
        // Also update the object in the array
        const targetIndex = this.targetsData.findIndex((t) => t.ID === sessionData.ID);
        if (targetIndex !== -1) {
          this.targetsData[targetIndex].IntValue = intValue;
        }
        
        // Also update in the session being edited
        if (this.sessionBeingEdited && this.sessionBeingEdited.TargetData) {
          const sessionTargetIndex = this.sessionBeingEdited.TargetData.findIndex(
            (t) => t.ID === sessionData.ID
          );
          if (sessionTargetIndex !== -1) {
            this.sessionBeingEdited.TargetData[sessionTargetIndex].IntValue = intValue;
          }
        }
      }
      if (this.targetIsFluencyRate(sessionData)) {
        const correctCount = this.fluencyCorrectCount() ?? 0;
        const incorrectCount = this.fluencyIncorrectCount() ?? 0;
        const durationInSeconds = this.minutesToSeconds(this.fluencyDurationInMinutes() ?? 0);

        sessionData.IntValue = correctCount;
        sessionData.TextValue = `${correctCount}/${incorrectCount}`;
        sessionData.Duration = durationInSeconds;

        const targetIndex = this.targetsData.findIndex((t) => t.ID === sessionData.ID);
        if (targetIndex !== -1) {
          this.targetsData[targetIndex].IntValue = sessionData.IntValue;
          this.targetsData[targetIndex].TextValue = sessionData.TextValue;
          this.targetsData[targetIndex].Duration = sessionData.Duration;
        }

        if (this.sessionBeingEdited && this.sessionBeingEdited.TargetData) {
          const sessionTargetIndex = this.sessionBeingEdited.TargetData.findIndex(
            (t) => t.ID === sessionData.ID
          );
          if (sessionTargetIndex !== -1) {
            this.sessionBeingEdited.TargetData[sessionTargetIndex].IntValue = sessionData.IntValue;
            this.sessionBeingEdited.TargetData[sessionTargetIndex].TextValue = sessionData.TextValue;
            this.sessionBeingEdited.TargetData[sessionTargetIndex].Duration = sessionData.Duration;
          }
        }
      }
    } else if (this.isPatientBehaviorDataModel(sessionData)) {
      if (this.behaviorIsDuration(sessionData)) {
        const intValueInSeconds = this.minutesToSeconds(this.behaviorDurationInMinutes());
        const textValue = this.minutesToFormattedString(this.behaviorDurationInMinutes());
        
        // Update the passed object
        sessionData.IntValue = intValueInSeconds;
        sessionData.TextValue = textValue;
        
        // Also update the object in the array to ensure it's saved correctly
        const behaviorIndex = this.behaviorsData.findIndex((b) => b.ID === sessionData.ID);
        if (behaviorIndex !== -1) {
          this.behaviorsData[behaviorIndex].IntValue = intValueInSeconds;
          this.behaviorsData[behaviorIndex].TextValue = textValue;
        }
        
        // Also update in the session being edited
        if (this.sessionBeingEdited && this.sessionBeingEdited.BehaviorData) {
          const sessionBehaviorIndex = this.sessionBeingEdited.BehaviorData.findIndex(
            (b) => b.ID === sessionData.ID
          );
          if (sessionBehaviorIndex !== -1) {
            this.sessionBeingEdited.BehaviorData[sessionBehaviorIndex].IntValue = intValueInSeconds;
            this.sessionBeingEdited.BehaviorData[sessionBehaviorIndex].TextValue = textValue;
          }
        }
      }
    }

    sessionData.ModifiedByUserID = this.authService.userInfo?.ID;

    console.log('Updated Session data:', sessionData);
    console.log('IntValue being saved:', sessionData.IntValue);
    this.saveSessionData(this.sessionBeingEdited!);
  }

  onSessionDataDelete(
    sessionData: PatientTargetDataModel | PatientBehaviorDataModel
  ) {
    console.log('Deleting session data:', sessionData);
    this.alertService
      .confirm(
        'Delete Session Data',
        'Are you sure you want to delete this session data?'
      )
      .subscribe((result) => {
        if (result) {
          this.deleteSessionData(sessionData);
        }
      });
  }

  onSessionDataEditCancel(
    sessionData: PatientTargetDataModel | PatientBehaviorDataModel
  ) {
    console.log('Cancelled editing session:', sessionData);

    if (this.isPatientTargetDataModel(sessionData)) {
      const index = this.sessionBeingEdited?.TargetData?.findIndex(
        (t) => t.ID === sessionData.ID
      );
      if (index === undefined) {
        console.error('Could not find index for session data:', sessionData);
        return;
      }

      this.sessionBeingEdited!.TargetData![index] = this.clonedSessionData[
        sessionData.ID as number
      ] as PatientTargetDataModel;
      delete this.clonedSessionData[sessionData.ID as number];
    } else {
      const index = this.sessionBeingEdited?.BehaviorData?.findIndex(
        (t) => t.ID === sessionData.ID
      );
      if (index === undefined) {
        console.error('Could not find index for session data:', sessionData);
        return;
      }

      this.sessionBeingEdited!.BehaviorData![index] = this.clonedSessionData[
        sessionData.ID as number
      ] as PatientBehaviorDataModel;
      delete this.clonedSessionData[sessionData.ID as number];
    }
  }

  saveSessionData(newSessionData: PatientSessionModel) {
    this.isLoading = true;
    console.log('Saving session data:', newSessionData);
    
    const currentUser = this.authService.userInfo;
    const currentUserID = currentUser?.ID;
    
    if (newSessionData) {
      // Update TargetData
      if (newSessionData.TargetData) {
        newSessionData.TargetData.forEach(targetData => {
          // Preserve original UserID if editing existing record, otherwise ensure UserID is set
          if (!targetData.UserID && currentUserID) {
            // New record without UserID: use current user (should be set from session staff)
            targetData.UserID = currentUserID;
            targetData.UserFullName = currentUser?.Fullnane ?? '';
          }
          // If editing existing record, UserID should already be set (original creator)
        });
      }
      
      // Update BehaviorData
      if (newSessionData.BehaviorData) {
        newSessionData.BehaviorData.forEach(behaviorData => {
          // Preserve original UserID if editing existing record, otherwise ensure UserID is set
          if (!behaviorData.UserID && currentUserID) {
            // New record without UserID: use current user (should be set from session staff)
            behaviorData.UserID = currentUserID;
            behaviorData.UserFullName = currentUser?.Fullnane ?? '';
          }
          // If editing existing record, UserID should already be set (original creator)
        });
      }
    }
    
    this.staffApiService
      .SavePatientSession({
        body: newSessionData,
      })
      .subscribe(
        (response) => {
          console.log('Saved session data:', response);
          
          // Update local data with the response to reflect ModifiedByUser and other server-side updates
          if (response) {
            // Update TargetData
            if (response.TargetData) {
              response.TargetData.forEach((updatedTargetData) => {
                const index = this.targetsData.findIndex((t) => t.ID === updatedTargetData.ID);
                if (index !== -1) {
                  // Merge the response data with the local data
                  this.targetsData[index] = {
                    ...this.targetsData[index],
                    ...updatedTargetData,
                    ModifiedByUser: updatedTargetData.ModifiedByUser,
                    ModifiedByUserID: updatedTargetData.ModifiedByUserID,
                    ModifiedDate: updatedTargetData.ModifiedDate,
                    ManuallyAddedByUser: updatedTargetData.ManuallyAddedByUser,
                    ManuallyAddedByUserID: updatedTargetData.ManuallyAddedByUserID,
                    ManuallyAddedDate: updatedTargetData.ManuallyAddedDate,
                  };
                } else if (!updatedTargetData.ID) {
                  // New record - add it to the array
                  this.targetsData.push(updatedTargetData);
                }
                
                // Also update in sessionBeingEdited if it exists
                if (this.sessionBeingEdited && this.sessionBeingEdited.TargetData) {
                  const sessionIndex = this.sessionBeingEdited.TargetData.findIndex((t) => t.ID === updatedTargetData.ID);
                  if (sessionIndex !== -1) {
                    this.sessionBeingEdited.TargetData[sessionIndex] = {
                      ...this.sessionBeingEdited.TargetData[sessionIndex],
                      ...updatedTargetData,
                      ModifiedByUser: updatedTargetData.ModifiedByUser,
                      ModifiedByUserID: updatedTargetData.ModifiedByUserID,
                      ModifiedDate: updatedTargetData.ModifiedDate,
                      ManuallyAddedByUser: updatedTargetData.ManuallyAddedByUser,
                      ManuallyAddedByUserID: updatedTargetData.ManuallyAddedByUserID,
                      ManuallyAddedDate: updatedTargetData.ManuallyAddedDate,
                    };
                  } else if (!updatedTargetData.ID) {
                    this.sessionBeingEdited.TargetData.push(updatedTargetData);
                  }
                }
              });
            }
            
            // Update BehaviorData
            if (response.BehaviorData) {
              response.BehaviorData.forEach((updatedBehaviorData) => {
                const index = this.behaviorsData.findIndex((b) => b.ID === updatedBehaviorData.ID);
                if (index !== -1) {
                  // Merge the response data with the local data
                  this.behaviorsData[index] = {
                    ...this.behaviorsData[index],
                    ...updatedBehaviorData,
                    ModifiedByUser: updatedBehaviorData.ModifiedByUser,
                    ModifiedByUserID: updatedBehaviorData.ModifiedByUserID,
                    ModifiedDate: updatedBehaviorData.ModifiedDate,
                    ManuallyAddedByUser: updatedBehaviorData.ManuallyAddedByUser,
                    ManuallyAddedByUserID: updatedBehaviorData.ManuallyAddedByUserID,
                    ManuallyAddedDate: updatedBehaviorData.ManuallyAddedDate,
                  };
                } else if (!updatedBehaviorData.ID) {
                  // New record - add it to the array
                  this.behaviorsData.push(updatedBehaviorData);
                }
                
                // Also update in sessionBeingEdited if it exists
                if (this.sessionBeingEdited && this.sessionBeingEdited.BehaviorData) {
                  const sessionIndex = this.sessionBeingEdited.BehaviorData.findIndex((b) => b.ID === updatedBehaviorData.ID);
                  if (sessionIndex !== -1) {
                    this.sessionBeingEdited.BehaviorData[sessionIndex] = {
                      ...this.sessionBeingEdited.BehaviorData[sessionIndex],
                      ...updatedBehaviorData,
                      ModifiedByUser: updatedBehaviorData.ModifiedByUser,
                      ModifiedByUserID: updatedBehaviorData.ModifiedByUserID,
                      ModifiedDate: updatedBehaviorData.ModifiedDate,
                      ManuallyAddedByUser: updatedBehaviorData.ManuallyAddedByUser,
                      ManuallyAddedByUserID: updatedBehaviorData.ManuallyAddedByUserID,
                      ManuallyAddedDate: updatedBehaviorData.ManuallyAddedDate,
                    };
                  } else if (!updatedBehaviorData.ID) {
                    this.sessionBeingEdited.BehaviorData.push(updatedBehaviorData);
                  }
                }
              });
            }
          }
          
          this.alertService.success('Session data saved successfully');
          this.isLoading = false;
        },
        (error) => {
          console.error('Error saving session data:', error);
          this.alertService.error(
            'Error saving session data - ' + error.Message
          );
          this.isLoading = false;
        }
      );
  }

  deleteSessionData(
    sessionData: PatientTargetDataModel | PatientBehaviorDataModel
  ) {
    this.isLoading = true;
    console.log('Deleting session data:', sessionData);
    if (this.isPatientTargetDataModel(sessionData)) {
      this.staffApiService
        .MarkTargetDataAsDeleted({
          patientTargetDataID: sessionData.ID,
        })
        .subscribe((response) => {
          console.log('Target data deleted:', response);
          this.isLoading = false;
          this.onSessionDataDeleted.emit(true);
        });
    } else {
      this.staffApiService
        .MarkBehaviorDataAsDeleted({
          patientBehaviorDataID: sessionData.ID!,
        })
        .subscribe((response) => {
          console.log('Behavior data deleted:', response);
          this.isLoading = false;
          this.onSessionDataDeleted.emit(true);
        });
    }
  }

  onBehaviorsRowExpand(event: TableRowExpandEvent) {
    console.log('Row expanded:', event);
  }

  onBehaviorsRowCollapse(event: TableRowCollapseEvent) {
    console.log('Row collapsed:', event);
  }

  behaviorAntecedentsOptions() {
    const antecedentsOptions = this.antecedents().map((a) => {
      return {
        value: a.ID,
        text: a.PatientBehaviorAntecedent,
      };
    });
    return antecedentsOptions;
  }

  behaviorConsequencesOptions() {
    const consequencesOptions = this.consequences().map((c) => {
      return {
        value: c.ID,
        text: c.PatientBehaviorConsequence,
      };
    });
    return consequencesOptions;
  }

  behaviorLocationsOptions() {
    const locationsOptions = this.locations().map((l) => {
      return {
        value: l.ID,
        text: l.PatientBehaviorLocation,
      };
    });
    return locationsOptions;
  }

  behaviorResponsesOptions() {
    const responsesOptions = this.responses().map((r) => {
      return {
        value: r.ID,
        text: r.PatientBehaviorResponse,
      };
    });
    return responsesOptions;
  }

  behaviorSeveritiesOptions() {
    const severitiesOptions = this.severities().map((s) => {
      return {
        value: s.ID,
        text: s.PatientBehaviorSeverity,
      };
    });
    return severitiesOptions;
  }

  targetIsToileting(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return target?.TargetType === TargetType.Toileting;
  }

  targetIsWholePartialInterval(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return target?.TargetType === TargetType.WholePartialInterval;
  }

  targetIsFluencyRate(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return target?.TargetType === TargetType.FluencyRate;
  }

  targetIsDuration(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return target?.TargetType === TargetType.Duration;
  }

  behaviorIsDuration(behaviorData: PatientBehaviorDataModel | undefined): boolean {
    if (!behaviorData) return false;
    const behavior = this.behaviors().find(
      (b) => b.ID === behaviorData.PatientBehaviorID
    );
    return behavior?.BehaviorType === PatientBehaviorType.Duration;
  }

  isPatientBehaviorDataModel(obj: any): obj is PatientBehaviorDataModel {
    return 'PatientBehaviorID' in obj && 'PatientSessionID' in obj;
  }

  intValueForDuration(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return 0;
    const target = this.targets().find(
      (t) => t.ID === targetData.PatientTargetID
    );
    return this.minutesToSeconds(targetData.IntValue!);
  }

  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  // format from seconds to hh:mm string
  minutesToFormattedString(minutes: number): string {
      const seconds = this.minutesToSeconds(minutes);
      const minutes1 = Math.floor((seconds % 3600) / 60);
      const seconds1 = seconds % 60;
      return `${minutes1 < 10 ? '0' + minutes1 : minutes1}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
  }

  updateSessionDataFluencyRate(sessionData: PatientTargetDataModel) {
    if (!sessionData.TextValue) return;
    const fluencyRate = sessionData.TextValue.split('/');
    sessionData.IntValue = parseInt(fluencyRate[0]);
  }
}
